import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { format } from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";

export function AnalyticsExportButton() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const {
    dateRange,
    filters,
    productFilter,
    includeAllOrders,
    isAnyFilterApplied,
  } = useAnalyticsFilters();
  const [isExporting, setIsExporting] = useState(false);

  const buildQueryParams = (filtered: boolean) => {
    const params: Record<string, unknown> = {
      fromTimestamp: dateRange.from,
      toTimestamp: dateRange.to,
    };

    if (filtered) {
      const resolution = filters.get("resolution")?.value.join(",");
      const reason = filters.get("reason")?.value.join(",");
      const country = filters.get("country")?.value.join(",");
      const sourceChannel = filters.get("sourceChannel")?.value.join(",");

      if (resolution) params.resolution = resolution;
      if (reason) params.reason = reason;
      if (country) params.country = country;
      if (sourceChannel) params.sourceChannel = sourceChannel;
      if (productFilter?.productId) params.productId = productFilter.productId;
      if (productFilter?.catalogId) params.catalogId = productFilter.catalogId;
      if (includeAllOrders) params.includeAllOrders = true;
    }

    return params;
  };

  const exportCsv = async (filtered: boolean) => {
    setIsExporting(true);
    try {
      const { data, error } = await fetchClient.GET(
        "/shops/{shopId}/statistics/products-return-rates",
        {
          params: {
            path: { shopId: shop.id },
            query: buildQueryParams(filtered),
          },
        },
      );

      if (error || !data?.products) return;

      const rows: {
        productId: string;
        product: string;
        variantId: string;
        variant: string;
        sku: string;
        ordersCount: number;
        returnsCount: number;
        returnRate: number;
      }[] = [];

      for (const product of data.products) {
        if (product.variants && product.variants.length > 0) {
          for (const variant of product.variants) {
            rows.push({
              productId: product.productId,
              product: product.title,
              variantId: variant.variantId,
              variant: variant.title,
              sku: variant.sku ?? "",
              ordersCount: variant.ordersCount,
              returnsCount: variant.returnsCount,
              returnRate: variant.returnRate,
            });
          }
        } else {
          rows.push({
            productId: product.productId,
            product: product.title,
            variantId: "",
            variant: "",
            sku: product.sku ?? "",
            ordersCount: product.ordersCount,
            returnsCount: product.returnsCount,
            returnRate: product.returnRate,
          });
        }
      }

      if (rows.length === 0) return;

      const escape = (value: unknown) => {
        if (value == null) return "";
        const str = value.toString();
        if (/[";,\n]/.test(str)) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const keys = [
        "productId",
        "product",
        "variantId",
        "variant",
        "sku",
        "ordersCount",
        "returnsCount",
        "returnRate",
      ] as const;
      const header = keys.join(";");
      const csvRows = rows
        .map((row) => keys.map((key) => escape(row[key])).join(";"))
        .join("\n");

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + header + "\n" + csvRows], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `analytics-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          disabled={isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isAnyFilterApplied && (
          <DropdownMenuItem onClick={() => exportCsv(true)}>
            {t("analytics.export.filtered")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => exportCsv(false)}>
          {t("analytics.export.all")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
